import { nhost } from '@/lib/nhost'

/**
 * Get a fresh access token for an authenticated fetch() to our own /api routes.
 *
 * The Nhost SDK has no `getAccessToken()`; the idiomatic call is `refreshSession(marginSeconds)`,
 * which returns the current session and only performs a network refresh if the access token is within
 * `margin` seconds of expiry (navigator.locks-guarded, cross-tab-deduped) — the same primitive the
 * Apollo auth link uses.
 *
 * WHY THIS MATTERS: the SDK's own graphql/functions/storage clients attach a fresh token via
 * `sessionRefreshMiddleware`, but a plain `fetch()` to a Next.js /api route does NOT — so if the page
 * has sat open past the ~15-min token lifetime, `useAuth().session.accessToken` is stale and the
 * route's `requireAuth()` → `/token/verify` rejects it ("Invalid or expired token"). Always mint the
 * token for these calls with `getFreshAccessToken()` (or `authedFetch`) instead of reading a
 * possibly-stale value off the React session.
 */
export async function getFreshAccessToken(): Promise<string | undefined> {
  try {
    const session = await nhost.refreshSession(60)
    return session?.accessToken
  } catch {
    // Transient refresh failure — fall back to the stored token; the request may 401 and re-auth.
    return nhost.getUserSession()?.accessToken
  }
}

/**
 * fetch() with a freshly-refreshed Bearer token attached. Use for authenticated calls to our own
 * /api routes from client code. Merges into any headers you pass.
 */
export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = await getFreshAccessToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return fetch(input, { ...init, headers })
}
